import axios from 'axios';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { COOKIES } from '../../services/exports/Constants';
import BackendApiClient from '../../services/api/BackendApiClient';
import {
  PROVIDER_CONTACT_TOAST_ID,
  isProviderContactRequired,
  providerContactMessage,
} from '../../services/helpers/ProviderContactError';
import BrandConfigManager from '../../services/api/brand/BrandConfigManager';
import ModifiersManager from '../../services/api/modifier/ModifiersManager';
import { InitialDataManager } from '../../services/api/InitialDataManager';
import ConfigManager from '../../services/api/ConfigManager';
import useAuth from '../auth/useAuth';
import CategoriesManager from '../../services/api/category/CategoriesManager';
import StatsManager from '../../services/api/stats/StatsManager';
import CompaniesManager from '../../services/api/company/CompaniesManager';
import DeliveryZonesManager from '../../services/api/company/DeliveryZonesManager';
import GalleryManager from '../../services/api/company/GalleryManager';
import MerchantProfileManager from '../../services/api/company/MerchantProfileManager';
import PaymentOptionsManager from '../../services/api/company/PaymentOptionsManager';
import CustomersManager from '../../services/api/customer/CustomersManager';
import FranchisesManager from '../../services/api/franchise/FranchisesManager';
import GoogleReviewsManager from '../../services/api/google-review/GoogleReviewsManager';
import MenusManager from '../../services/api/menu/MenusManager';
import MonthlyStatementsManager from '../../services/api/monthly-statement/MonthlyStatementsManager';
import NotificationsManager from '../../services/api/notification/NotificationsManager';
import OnboardingStepsManager from '../../services/api/onboarding-step/OnboardingStepsManager';
import ModifierGroupsManager from '../../services/api/modifier-group/ModifierGroupsManager';
import OrdersManager from '../../services/api/order/OrdersManager';
import PayoutsManager from '../../services/api/payout/PayoutsManager';
import PrintersManager from '../../services/api/printer/PrintersManager';
import ProductsManager from '../../services/api/product/ProductsManager';
import PromoCodesManager from '../../services/api/promo-code/PromoCodesManager';
import PromoCodeComponentsManager from '../../services/api/promo-code-component/PromoCodeComponentsManager';
import PromotionsManager from '../../services/api/promotion/PromotionsManager';
import ReceiptsManager from '../../services/api/receipt/ReceiptsManager';
import ReportsManager from '../../services/api/report/ReportsManager';
import ServiceContractsManager from '../../services/api/service-contract/ServiceContractsManager';
import StaffAccountManager from '../../services/api/staff-account/StaffAccountManager';
import StripeManager from '../../services/api/stripe/StripeManager';
import SubscriptionsManager from '../../services/api/subscription/SubscriptionsManager';
import SubscriptionProductsManager from '../../services/api/subscription-product/SubscriptionProductsManager';
import SubscriptionProductPricesManager from '../../services/api/subscription-product-price/SubscriptionProductPricesManager';
import StorefrontManager from '../../services/api/storefront/StorefrontManager';
import StorefrontDashboardManager from '../../services/api/storefront/dashboard/StorefrontManager';
import WebsiteStudioManager from '../../services/api/website-studio/WebsiteStudioManager';
import WebsiteStudioV2Manager from '../../services/api/website-studio/WebsiteStudioV2Manager';
import CurrentCompanyManager from '../../services/api/company/CurrentCompanyManager';
import useAppCookies from '../utility/useAppCookies';
import PolygraphyManager from '../../services/api/polygraphy/PolygraphyManager';
import SetupPackageFulfillmentsManager from '../../services/api/setup-package-fulfillments/SetupPackageFulfillmentsManager';
import OpenAiManager from '../../services/api/open-ai/OpenAiManager';
import ReservationsManager from '../../services/api/reservation/ReservationsManager';
import ReservationScheduleExceptionManager from '../../services/api/reservation/ScheduleExceptionManager';
import ReservationAmendScheduleExceptionManager from '../../services/api/reservation/AmendScheduleExceptionManager';
import ReservationScriptManager from '../../services/api/reservation/ScriptManager';
import CompanyScheduleExceptionManager from '../../services/api/schedule-exception/ScheduleExceptionManager';
import CompanyAmendScheduleExceptionManager from '../../services/api/schedule-exception/AmendScheduleExceptionManager';
import ServiceProvidersManager from '../../services/api/service-provider/ServiceProvidersManager';
import PartnerSettingsManager from '../../services/api/service-provider/PartnerSettingsManager';
import PartnerDashboardDomainManager from '../../services/api/service-provider/PartnerDashboardDomainManager';
import PartnerShopDomainsManager from '../../services/api/service-provider/PartnerShopDomainsManager';
import HostAccessManager from '../../services/api/auth/HostAccessManager';
import PartnerGatewaysManager from '../../services/api/service-provider/PartnerGatewaysManager';
import ServiceProviderBrandManager from '../../services/api/service-provider/ServiceProviderBrandManager';
import ServiceProviderProfileManager from '../../services/api/service-provider/ServiceProviderProfileManager';
import ServiceProviderFeatureFlagsManager from '../../services/api/service-provider/ServiceProviderFeatureFlagsManager';
import ServiceProviderNotificationSettingsManager from '../../services/api/service-provider/ServiceProviderNotificationSettingsManager';
import ServiceProviderSettingsManager from '../../services/api/service-provider/ServiceProviderSettingsManager';
import SpreedlyManager from '../../services/api/spreedly/SpreedlyManager';
import ProviderBillingPriceBookManager from '../../services/api/provider-billing/ProviderBillingPriceBookManager';
import ProviderBillingLicensesManager from '../../services/api/provider-billing/ProviderBillingLicensesManager';
import PartnerLicenseProductsManager from '../../services/api/provider-billing/PartnerLicenseProductsManager';
import PartnerLicensesManager from '../../services/api/provider-billing/PartnerLicensesManager';
import TestRestaurantsManager from '../../services/api/provider-billing/TestRestaurantsManager';
import ProviderBillingInvoicesManager from '../../services/api/provider-billing/ProviderBillingInvoicesManager';
import ProviderBillingSettlementsManager from '../../services/api/provider-billing/ProviderBillingSettlementsManager';
import PartnerBillingManager from '../../services/api/provider-billing/PartnerBillingManager';
import ServiceFeeSettingsManager from '../../services/api/service-fee/ServiceFeeSettingsManager';
import ServiceFeeDecisionManager from '../../services/api/service-fee/ServiceFeeDecisionManager';
import TaxCategoriesManager from '../../services/api/tax-category/TaxCategoriesManager';
import StripeProcessingFeeRatesManager from '../../services/api/processing-fee/StripeProcessingFeeRatesManager';
import TaxSettingsManager from '../../services/api/tax-settings/TaxSettingsManager';
import CompanyReservationManager from '../../services/api/reservation/CompanyReservationManager';
import DashboardReservationManager from '../../services/api/reservation/DashboardReservationManager';
import RoomServiceLocationsManager from '../../services/api/room-service-location/RoomServiceLocationsManager';
import DuplicatesManager from '../../services/api/menu/DuplicatesManager';
import InvoicesManager from '../../services/api/invoice/InvoicesManager';
import SmartPricingRulesManager from '../../services/api/smart-pricing-rules/SmartPricingRulesManager';
import ManagersManager from '../../services/api/team/ManagersManager';
import TeamReportsManager from '../../services/api/team/TeamReportsManager';
import MagicLinksManager from '../../services/api/magic-link/MagicLinksManager';
import DeliveriesManager from '../../services/api/delivery/DeliveriesManager';
import WoltDriveOnboardingManager from '../../services/api/delivery/WoltDriveOnboardingManager';
import ReservationRuleManager from '../../services/api/reservation/ReservationRuleManager';
import ReservationMediaManager from '../../services/api/reservation/ReservationMediaManager';
import EmailMarketingManager from '../../services/api/email-marketing/MarketingEmailsManager';
import GoogleBusinessManager from '../../services/api/google-business/GoogleBusinessManager';
import MarketingSettingsManager from '../../services/api/marketing/MarketingSettingsManager';
import MarketingOverviewManager from '../../services/api/marketing/MarketingOverviewManager';
import CustomerBaseStatsManager from '../../services/api/customer-base/CustomerBaseStatsManager';
import BusinessProfilesManager from '../../services/api/business-profile/BusinessProfilesManager';
import BusinessDetailsManager from '../../services/api/business-profile/BusinessDetailsManager';
import CompanyPaymentSetupManager from '../../services/api/company/CompanyPaymentSetupManager';
import ImpressumManager from '../../services/api/impressum/ImpressumManager';
import ManagerProfilesManager from '../../services/api/manager/ManagerProfilesManager';
import GoogleAdsManager from '../../services/api/google-ads/GoogleAdsManager';
import TrackingLinksManager from '../../services/api/tracking-link/TrackingLinksManager';
import FeaturedPostsManager from '../../services/api/featured-posts/FeaturedPostsManager';
import MobileAppManager from '../../services/api/mobile-app/MobileAppManager';
import FranchiseMobileAppManager from '../../services/api/mobile-app/FranchiseMobileAppManager';
import MobileAppPushNotificationManager from '../../services/api/mobile-app/PushNotificationManager';
import ShopDomainsManager from '../../services/api/shop-domain/ShopDomainsManager';
import PhoneAnsweringManager from '../../services/api/phone-answering/PhoneAnsweringManager';
import MobileAppCampaignManager from '../../services/api/mobile-app/CampaignManager';
import MobileAppStatsManager from '../../services/api/mobile-app/StatsManager';
import MobileAppUserSettingManager from '../../services/api/mobile-app/UserSettingManager';
import OfferManager from '../../services/api/offer/OfferManager';
import PaymentGatewaysManager from '../../services/api/payment-gateway/PaymentGatewaysManager';
import GiftCardManager from '../../services/api/gift-card/GiftCardManager';
import CurrentCompanyInfoManager from '../../services/api/company/CurrentCompanyInfoManager';
import DevelopersManager from '../../services/api/developer/DevelopersManager';
import MaxwellTicketsManager from '../../services/api/maxwell/MaxwellTicketsManager';
import FeatureFlagsManager from '../../services/api/feature-flag/FeatureFlagsManager';
import MenuEditHistoryManager from '../../services/api/menu/MenuEditHistoryManager';
import PaypalConnectionManager from '../../services/api/payment/PaypalConnectionManager';
import UserDeletionRequestsManager from '../../services/api/user-deletion-request/UserDeletionRequestsManager';
import usePermissions from '../auth/usePermissions';
import OpsQueueManager from '../../services/api/agent-ops/OpsQueueManager';
import { selectCurrentFranchiseId } from '../../store/slices/CurrentFranchiseSlice';

export default function useApiClient({ baseUrl = null, autoLogOut = true } = {}) {
  const { i18n } = useTranslation();
  // Memoised on the language itself: the i18n object keeps its identity across a
  // language switch, so keying on it left Shop answering in the previous language.
  const language = i18n.language;
  const { getCookie } = useAppCookies([COOKIES.AUTH_TOKEN]);

  const token = getCookie(COOKIES.AUTH_TOKEN);
  const { isMerchant } = usePermissions();

  const { company } = useSelector((state) => state.currentCompany);
  const franchiseId = useSelector(selectCurrentFranchiseId);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const onBehalfOfCompany = company?.id ?? '';
  const onBehalfOfFranchise = (isMerchant ? (franchiseId ?? company?.franchise_id) : company?.franchise_id) ?? '';
  const onBehalfOfServiceProvider = serviceProvider?.id ?? '';

  const { logOut } = useAuth();

  const config = useMemo(
    () => ({
      baseURL: baseUrl ?? import.meta.env.VITE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'x-on-behalf-of-company': onBehalfOfCompany,
        'x-on-behalf-of-franchise': onBehalfOfFranchise,
        'x-on-behalf-of-service-provider': onBehalfOfServiceProvider,
        'x-preferred-locale': language,
      },
    }),
    [baseUrl, token, onBehalfOfCompany, onBehalfOfServiceProvider, language, onBehalfOfFranchise],
  );

  const client = useMemo(() => {
    const instance = axios.create(config);
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          autoLogOut && logOut();
        }

        // A PLG partner's restaurant user reached a decision the partner keeps
        // (a stale bundle, a deep link, a screen that missed the gate): say whom
        // to ask, once however many calls a save made, from wherever it came.
        if (error.response?.status === 403 && isProviderContactRequired(error.response.data)) {
          toast.error(providerContactMessage(error.response.data), { toastId: PROVIDER_CONTACT_TOAST_ID });
        }

        return Promise.reject(error);
      },
    );

    return instance;
  }, [config, autoLogOut]);

  const clientInstance = useMemo(() => new BackendApiClient(client), [client]);

  return useMemo(
    () => ({
      config,
      client,
      clientInstance,
      BrandConfigManager: new BrandConfigManager(clientInstance),
      BusinessProfilesManager: new BusinessProfilesManager(clientInstance),
      BusinessDetailsManager: new BusinessDetailsManager(clientInstance),
      CompanyPaymentSetupManager: new CompanyPaymentSetupManager(clientInstance),
      ImpressumManager: new ImpressumManager(clientInstance),
      CategoriesManager: new CategoriesManager(clientInstance),
      CompaniesManager: new CompaniesManager(clientInstance),
      CurrentCompanyManager: new CurrentCompanyManager(clientInstance),
      DeliveriesManager: new DeliveriesManager(clientInstance),
      WoltDriveOnboardingManager: new WoltDriveOnboardingManager(clientInstance),
      DeliveryZonesManager: new DeliveryZonesManager(clientInstance),
      GalleryManager: new GalleryManager(clientInstance),
      MerchantProfileManager: new MerchantProfileManager(clientInstance),
      PaymentOptionsManager: new PaymentOptionsManager(clientInstance),
      CustomersManager: new CustomersManager(clientInstance),
      FranchisesManager: new FranchisesManager(clientInstance),
      GoogleReviewsManager: new GoogleReviewsManager(clientInstance),
      GoogleBusinessManager: new GoogleBusinessManager(clientInstance),
      GoogleAdsManager: new GoogleAdsManager(clientInstance),
      InvoicesManager: new InvoicesManager(clientInstance),
      MagicLinksManager: new MagicLinksManager(clientInstance),
      ManagerProfilesManager: new ManagerProfilesManager(clientInstance),
      DuplicatesManager: new DuplicatesManager(clientInstance),
      MenusManager: new MenusManager(clientInstance),
      ModifiersManager: new ModifiersManager(clientInstance),
      ModifierGroupsManager: new ModifierGroupsManager(clientInstance),
      MonthlyStatementsManager: new MonthlyStatementsManager(clientInstance),
      NotificationsManager: new NotificationsManager(clientInstance),
      OnboardingStepsManager: new OnboardingStepsManager(clientInstance),
      OrdersManager: new OrdersManager(clientInstance),
      PayoutsManager: new PayoutsManager(clientInstance),
      PrintersManager: new PrintersManager(clientInstance),
      ProductsManager: new ProductsManager(clientInstance),
      PromoCodesManager: new PromoCodesManager(clientInstance),
      PromoCodeComponentsManager: new PromoCodeComponentsManager(clientInstance),
      PromotionsManager: new PromotionsManager(clientInstance),
      ReceiptsManager: new ReceiptsManager(clientInstance),
      ReportsManager: new ReportsManager(clientInstance),
      ReservationsManager: new ReservationsManager(clientInstance),
      ReservationsRuleManager: new ReservationRuleManager(clientInstance),
      ReservationMediaManager: new ReservationMediaManager(clientInstance),
      CompanyReservationManager: new CompanyReservationManager(clientInstance),
      DashboardReservationManager: new DashboardReservationManager(clientInstance),
      ReservationScheduleExceptionManager: new ReservationScheduleExceptionManager(clientInstance),
      ReservationAmendScheduleExceptionManager: new ReservationAmendScheduleExceptionManager(clientInstance),
      ReservationScriptManager: new ReservationScriptManager(clientInstance),
      CompanyScheduleExceptionManager: new CompanyScheduleExceptionManager(clientInstance),
      CompanyAmendScheduleExceptionManager: new CompanyAmendScheduleExceptionManager(clientInstance),
      RoomServiceLocationsManager: new RoomServiceLocationsManager(clientInstance),
      ServiceContractsManager: new ServiceContractsManager(clientInstance),
      ServiceProvidersManager: new ServiceProvidersManager(clientInstance),
      PartnerSettingsManager: new PartnerSettingsManager(clientInstance),
      PartnerDashboardDomainManager: new PartnerDashboardDomainManager(clientInstance),
      PartnerShopDomainsManager: new PartnerShopDomainsManager(clientInstance),
      HostAccessManager: new HostAccessManager(clientInstance),
      PartnerGatewaysManager: new PartnerGatewaysManager(clientInstance),
      ServiceProviderBrandManager: new ServiceProviderBrandManager(clientInstance),
      ServiceProviderProfileManager: new ServiceProviderProfileManager(clientInstance),
      ServiceProviderFeatureFlagsManager: new ServiceProviderFeatureFlagsManager(clientInstance),
      ServiceProviderNotificationSettingsManager: new ServiceProviderNotificationSettingsManager(clientInstance),
      ServiceProviderSettingsManager: new ServiceProviderSettingsManager(clientInstance),
      SpreedlyManager: new SpreedlyManager(clientInstance),
      ProviderBillingPriceBookManager: new ProviderBillingPriceBookManager(clientInstance),
      ProviderBillingLicensesManager: new ProviderBillingLicensesManager(clientInstance),
      PartnerLicenseProductsManager: new PartnerLicenseProductsManager(clientInstance),
      PartnerLicensesManager: new PartnerLicensesManager(clientInstance),
      TestRestaurantsManager: new TestRestaurantsManager(clientInstance),
      ProviderBillingInvoicesManager: new ProviderBillingInvoicesManager(clientInstance),
      ProviderBillingSettlementsManager: new ProviderBillingSettlementsManager(clientInstance),
      PartnerBillingManager: new PartnerBillingManager(clientInstance),
      ServiceFeeSettingsManager: new ServiceFeeSettingsManager(clientInstance),
      ServiceFeeDecisionManager: new ServiceFeeDecisionManager(clientInstance),
      TaxCategoriesManager: new TaxCategoriesManager(clientInstance),
      StripeProcessingFeeRatesManager: new StripeProcessingFeeRatesManager(clientInstance),
      TaxSettingsManager: new TaxSettingsManager(clientInstance),
      SetupPackageFulfillmentsManager: new SetupPackageFulfillmentsManager(clientInstance),
      SmartPricingRulesManager: new SmartPricingRulesManager(clientInstance),
      StaffAccountManager: new StaffAccountManager(clientInstance),
      StatsManager: new StatsManager(clientInstance),
      StorefrontManager: new StorefrontManager(clientInstance),
      StorefrontDashboardManager: new StorefrontDashboardManager(clientInstance),
      WebsiteStudioManager: new WebsiteStudioManager(clientInstance),
      WebsiteStudioV2Manager: new WebsiteStudioV2Manager(clientInstance),
      OpenAiManager: new OpenAiManager(clientInstance),
      StripeManager: new StripeManager(clientInstance),
      SubscriptionsManager: new SubscriptionsManager(clientInstance),
      SubscriptionProductsManager: new SubscriptionProductsManager(clientInstance),
      SubscriptionProductPricesManager: new SubscriptionProductPricesManager(clientInstance),
      ManagersManager: new ManagersManager(clientInstance),
      TeamReportsManager: new TeamReportsManager(clientInstance),
      InitialDataManager: new InitialDataManager(clientInstance),
      ConfigManager: new ConfigManager(clientInstance),

      EmailMarketingManager: new EmailMarketingManager(clientInstance),
      MarketingSettingsManager: new MarketingSettingsManager(clientInstance),
      MarketingOverviewManager: new MarketingOverviewManager(clientInstance),
      TrackingLinksManager: new TrackingLinksManager(clientInstance),

      CustomerBaseStatsManager: new CustomerBaseStatsManager(clientInstance),

      MobileAppManager: new MobileAppManager(clientInstance),
      FranchiseMobileAppManager: new FranchiseMobileAppManager(clientInstance),
      MobileAppPushNotificationManager: new MobileAppPushNotificationManager(clientInstance),
      MobileAppCampaignManager: new MobileAppCampaignManager(clientInstance),
      MobileAppStatsManager: new MobileAppStatsManager(clientInstance),
      MobileAppUserSettingManager: new MobileAppUserSettingManager(clientInstance),

      FeaturedPostsManager: new FeaturedPostsManager(clientInstance),
      ShopDomainsManager: new ShopDomainsManager(clientInstance),

      PhoneAnsweringManager: new PhoneAnsweringManager(clientInstance),
      OfferManager: new OfferManager(clientInstance),
      PaymentGatewaysManager: new PaymentGatewaysManager(clientInstance),
      PolygraphyManager: new PolygraphyManager(clientInstance),
      GiftCardManager: new GiftCardManager(clientInstance),
      CurrentCompanyInfoManager: new CurrentCompanyInfoManager(clientInstance),
      DevelopersManager: new DevelopersManager(clientInstance),
      MaxwellTicketsManager: new MaxwellTicketsManager(clientInstance),
      FeatureFlagsManager: new FeatureFlagsManager(clientInstance),
      MenuEditHistoryManager: new MenuEditHistoryManager(clientInstance),
      PaypalConnectionManager: new PaypalConnectionManager(clientInstance),
      UserDeletionRequestsManager: new UserDeletionRequestsManager(clientInstance),

      OpsQueueManager: new OpsQueueManager(clientInstance),
    }),
    [config, client, clientInstance],
  );
}
